import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useMisAuth } from "../contexts/misAuth";
import axiosInstance from "../utils/axiosConfig";
import { fetchAuthToken } from "../utils/getCognitoToken";

const MisLogin = () => {
  const { onMisLogin } = useMisAuth();
  const [otpSent, setOtpSent] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    mobileNo: "",
    otp: "",
    idpId: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [referenceId, setReferenceId] = useState("");
  const [otpAttempts, setOtpAttempts] = useState(0);
  const [nextOtpTime, setNextOtpTime] = useState(null);
  const navigate = useNavigate();

  // Rate limiting constants
  const MAX_OTP_ATTEMPTS = 200;
  const OTP_COOLDOWN_PERIOD = 5 * 60 * 1000; // 5 minutes in milliseconds

  // Email validation regex
  const EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

  useEffect(() => {
    // Check for stored rate limiting data
    const storedData = localStorage.getItem("otpRateLimit");
    if (storedData) {
      const { attempts, nextTime } = JSON.parse(storedData);
      setOtpAttempts(attempts);
      setNextOtpTime(nextTime);
    }
  }, []);

  const updateRateLimitData = (attempts, nextTime) => {
    setOtpAttempts(attempts);
    setNextOtpTime(nextTime);
    localStorage.setItem(
      "otpRateLimit",
      JSON.stringify({
        attempts,
        nextTime,
      })
    );
  };

  const validateForm = () => {
    // Email validation
    if (!EMAIL_REGEX.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }

    // IDP ID validation
    // if (!IDP_ID_REGEX.test(formData.idpId)) {
    //   toast.error("Please enter a valid 4-digit IDP ID");
    //   return false;
    // }

    // Mobile number validation (optional field)
    // if (formData.mobileNo && !/^\d{10}$/.test(formData.mobileNo)) {
    //   toast.error("Please enter a valid 10-digit mobile number");
    //   return false;
    // }

    return true;
  };

  const checkRateLimit = () => {
    const currentTime = Date.now();

    // Reset attempts if cooldown period has passed
    if (nextOtpTime && currentTime > nextOtpTime) {
      updateRateLimitData(0, null);
      return true;
    }

    // Check if maximum attempts reached
    if (otpAttempts >= MAX_OTP_ATTEMPTS) {
      const timeLeft = Math.ceil((nextOtpTime - currentTime) / 1000 / 60);
      toast.error(`Too many attempts. Please try again in ${timeLeft} minutes`);
      return false;
    }

    return true;
  };

  const generateRequestId = () => {
    return Math.random().toString(36).substring(2, 15);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Real-time validation for IDP ID
    if (name === "idpId" && value.length > 4) {
      return; // Prevent input of more than 4 digits
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGenerateOTP = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    axiosInstance()
      .post("auth/generate-otp", {
        mobileNo: formData.mobileNo,
        emailId: formData.email,
        otpType: "email_otp",
        loginContext: "mis",
      })
      .then((res) => {
        if ([200, 1136].includes(res.data.code)) {
          setReferenceId(res.data.referenceId);
          setOtpSent(true);
          toast.success("OTP sent successfully!");
        } else {
          // Handle specific error codes
          if (res.data.code === 1151) {
            // toast.error("User does not exist in the system");
            toast.error(
              res.data.message || "User does not exist in the system"
            );
          } else {
            toast.error(res.data.message || "Failed to send OTP");
          }
        }
      })
      .catch((err) => {
        console.log(err);
        // Check if the error response contains our specific error
        if (
          err.response &&
          err.response.data &&
          err.response.data.code === 1151
        ) {
          // toast.error("User does not exist in the system");
          toast.error(
            err.response.data.message || "User does not exist in the system"
          );
        } else {
          toast.error(err.toString());
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await axiosInstance().post("auth/mis-user-login", {
        emailId: formData.email,
        emailIdOtp: formData.otp,
        idpId: formData.idpId,
        mobileNo: formData.mobileNo, // Fixed typo: was "mobielNo"
        referenceId,
      });

      // Security fix: Check both HTTP status AND response code AND required fields
      if (
        res.status !== 200 ||
        res?.data?.code !== 200 ||
        !res?.data?.emailId ||
        !res?.data?.userName
      ) {
        toast.error(res?.data?.message || "Login failed");
        return;
      }

      // Fetch Cognito token for API Gateway
      const accessToken = await fetchAuthToken();
      if (!accessToken) {
        toast.error("Could not fetch access token. Please try again.");
        return; // abort login to avoid broken session as everything will fail if the access token is not fetched
      }

      // Persist and continue
      localStorage.setItem("misUserEmail", formData.email);
      localStorage.setItem("misUserIdpId", formData.idpId);
      localStorage.setItem("loginTime", Date.now().toString());
      localStorage.setItem("access_token", accessToken);

      onMisLogin("/mis-user", {
        email: formData.email,
        idpId: formData.idpId,
        loginTime: Date.now(),
      });
      toast.success("Login successful!");
      navigate("/mis-user");
    } catch (err) {
      console.log(err);
      // Handle HTTP 401 (authentication failure)
      if (err.response?.status === 401) {
        const errorData = err.response.data;
        // Handle specific OTP error codes
        if (errorData.code === 1143) {
          toast.error("OTP is incorrect. Please try again.");
        } else if (errorData.code === 1144) {
          toast.error("OTP has expired. Please request a new one.");
          setOtpSent(false); // Allow user to request new OTP
        } else if (errorData.code === 1150) {
          toast.error("User not found or invalid credentials");
        } else {
          toast.error(errorData.message || "Authentication failed");
        }
      } else {
        // Handle other HTTP errors
        toast.error("Login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main
      className="d-flex align-items-center justify-content-center bg-light"
      style={{ paddingBottom: "100px" }}
    >
      <div
        className="card rounded-lg border-0 shadow-sm mt-5"
        style={{ width: "400px" }}
      >
        <div className="card-body p-4">
          <h2
            className="text-center mb-4"
            style={{
              fontSize: "24px",
              fontWeight: "600",
              color: "#196BD1",
            }}
          >
            MIS Login
          </h2>

          <form onSubmit={otpSent ? handleVerifyOTP : handleGenerateOTP}>
            <div className="mb-3">
              <label className="form-label mb-2">Email Address</label>
              <input
                type="email"
                name="email"
                required
                placeholder="eg. john.doe@gmail.com"
                value={formData.email}
                onChange={handleChange}
                disabled={otpSent}
                className={`form-control ${
                  formData.email && !EMAIL_REGEX.test(formData.email)
                    ? "is-invalid"
                    : ""
                }`}
                style={{
                  backgroundColor: otpSent ? "#F5F7FA" : "#fff",
                }}
              />
              {formData.email && !EMAIL_REGEX.test(formData.email) && (
                <div className="invalid-feedback">
                  Please enter a valid email address
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label mb-2">IDP ID</label>
              <input
                type="text"
                name="idpId"
                required
                maxLength="4"
                placeholder="eg. 0001"
                value={formData.idpId}
                onChange={handleChange}
                disabled={otpSent}
                className={`form-control ${
                  formData.idpId
                  //  && !IDP_ID_REGEX.test(formData.idpId) ? 'is-invalid' : ''
                }`}
                style={{
                  backgroundColor: otpSent ? "#F5F7FA" : "#fff",
                }}
              />
              {formData.idpId && (
                // !IDP_ID_REGEX.test(formData.idpId) &&
                <div className="invalid-feedback">
                  Please enter a valid 4-digit IDP ID
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label mb-2">
                Mobile Number (Optional)
              </label>
              <input
                type="tel"
                name="mobileNo"
                placeholder="eg. 88888 00000"
                value={formData.mobileNo}
                onChange={handleChange}
                disabled={otpSent}
                className="form-control"
                style={{
                  backgroundColor: otpSent ? "#F5F7FA" : "#fff",
                }}
              />
            </div>

            <div
              style={{
                maxHeight: otpSent ? "100px" : "0",
                overflow: "hidden",
                transition: "all linear 0.2s",
                marginBottom: otpSent ? "1rem" : "0",
              }}
            >
              <label className="form-label mb-2">Enter OTP</label>
              <input
                type="text"
                name="otp"
                required={otpSent}
                placeholder="Enter OTP sent to your email"
                value={formData.otp}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || (nextOtpTime && Date.now() < nextOtpTime)}
              className="btn btn-dark w-100"
              style={{
                transition: "all linear 0.2s",
                opacity:
                  isLoading || (nextOtpTime && Date.now() < nextOtpTime)
                    ? 0.3
                    : 1,
              }}
            >
              {isLoading
                ? otpSent
                  ? "Verifying..."
                  : "Sending OTP..."
                : nextOtpTime && Date.now() < nextOtpTime
                ? `Try again in ${Math.ceil(
                    (nextOtpTime - Date.now()) / 1000 / 60
                  )} minutes`
                : otpSent
                ? "Verify & Login"
                : "Send OTP"}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
};

export default MisLogin;
